import { Customer } from "gen/customer/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { BoolFilter } from "gen/search/service/service_pb"
import { ReactNode, useMemo } from "react"
import { UUID } from "shared-ts/classes/uuid"
import { Chip } from "shared-web/components/base/informational/Chip"
import {
  AccessoryContext,
  usePopoverSelect,
  UsePopoverSelectProps,
  UsePopoverSelectRes,
} from "shared-web/components/composite/popover"

import { useSearchJobs } from "features/api/search"
import { urls } from "urls"

interface UseJobsPopoverSelectProps
  extends Pick<
    UsePopoverSelectProps<Customer>,
    "selected" | "onSelect" | "defaultSelected"
  > {
  customerId?: PBUUID
  filter?: (job: Customer) => boolean
}

export type UseJobsPopoverSelectRes = UsePopoverSelectRes<Customer>

function useJobsPopoverSelect(
  props: UseJobsPopoverSelectProps
): UseJobsPopoverSelectRes {
  const { customerId, filter } = props

  const { results, setQuery } = useSearchJobs(
    {
      customerIds: customerId ? [customerId] : [],
      isActiveFilter: BoolFilter.TRUE,
    },
    { ignoreSearchQuery: true }
  )

  const jobs = useMemo(() => {
    const res: Customer[] = []
    for (const r of results) {
      if (!r.job) continue
      if (filter && !filter(r.job)) continue
      res.push(r.job)
    }
    return res
  }, [results, filter])

  return usePopoverSelect({
    items: jobs,
    keyExtractor,
    labelExtractor,
    accessoryExtractor,
    onChangeQuery: setQuery,
    ...props,
  })
}

function keyExtractor(job: Customer): string {
  return UUID.idString(job.id)
}

function labelExtractor(job: Customer): string {
  return job.name
}

function accessoryExtractor(
  job: Customer,
  context: AccessoryContext
): ReactNode | null {
  if (!job.identifier) {
    return null
  }
  if (context === "button") {
    return (
      <span onClick={(e) => e.stopPropagation()}>
        <Chip color="sky" text={job.identifier} href={urls.job(job.id)} />
      </span>
    )
  }
  return <Chip color="sky" text={job.identifier} />
}

export default useJobsPopoverSelect
